import { useServices } from '@/app/providers/ServiceContext';
import { useToast, useConfirmation } from '@/app/providers';
import { Unit, UnitType, User } from '@/shared/types';
import type { PaginatedResponse } from '@yawi/domain';
import { Check, ChevronLeft, Loader, Pencil, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../atoms/Button';
import { Checkbox } from '../atoms/Checkbox';
import { Input } from '../atoms/Input';
import { Select } from '../atoms/Select';
import {
  UnitFormFields,
  UnitFormState,
  buildUnitName,
  useUnitSectionRequired,
} from '../molecules/UnitFormFields';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ResidentOnboardingWizardProps {
  onClose: () => void;
  onCompleted: (resident: User) => void;
}


interface ResidentForm {
  name: string;
  email: string;
  phone: string;
  password: string;
  isPrincipal: boolean;
}

interface RfidForm {
  tagId: string;
  label: string;
  cardType: string;
  skipRfid: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getUnitLabel = (unit: Unit): string => {
  if (unit.name) return unit.name;
  if (unit.tower && unit.number) return `Torre ${unit.tower} - ${unit.number}`;
  if (unit.street) return `${unit.street}${unit.exteriorNumber ? ` #${unit.exteriorNumber}` : ''}`;
  return unit.id;
};

const getUnitSubLabel = (unit: Unit): string => {
  const parts: string[] = [];
  if (unit.block) parts.push(`Manzana ${unit.block}`);
  if (unit.floor) parts.push(`Piso ${unit.floor}`);
  return parts.join(' · ');
};

const isPasswordStrong = (pwd: string) =>
  pwd.length >= 8 && /[A-Z]/.test(pwd) && /[a-z]/.test(pwd) && /[0-9]/.test(pwd) && /[^A-Za-z0-9]/.test(pwd);

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// ─── Step Indicator ───────────────────────────────────────────────────────────

const StepIndicator: React.FC<{ current: number }> = ({ current }) => {
  const steps = ['Unidad', 'Residente', 'RFID', 'Confirmar'];
  return (
    <div className="flex items-center gap-0 px-6 py-4 border-b border-slate-100">
      {steps.map((label, idx) => {
        const step = idx + 1;
        const active = step === current;
        const done = step < current;
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  done
                    ? 'bg-emerald-500 text-white'
                    : active
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-200 text-slate-500'
                }`}
              >
                {done ? <Check size={12} /> : step}
              </div>
              <span className={`text-[10px] font-medium ${active ? 'text-primary-600' : 'text-slate-400'}`}>
                {label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 mt-[-14px] ${step < current ? 'bg-emerald-400' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─── Step 1 — Unit Selector ───────────────────────────────────────────────────

const Step1UnitSelector: React.FC<{
  selectedUnit: Unit | null;
  onSelect: (unit: Unit) => void;
  newUnitMode: boolean;
  onToggleNewUnit: (v: boolean) => void;
  newUnitForm: UnitFormState;
  onChangeNewUnit: (f: UnitFormState) => void;
}> = ({ selectedUnit, onSelect, newUnitMode, onToggleNewUnit, newUnitForm, onChangeNewUnit }) => {
  const { unitRepo } = useServices();
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    // El backend cap el limit a 100 (MAX_PAGE_SIZE). Para condos con >100 unidades
    // hay que paginar hasta agotar — si no, solo aparecerían las primeras 100.
    const fetchAllUnits = async () => {
      const accumulated: Unit[] = [];
      const PAGE_LIMIT = 100;
      const SAFETY_MAX_PAGES = 30; // tope duro a 3000 unidades
      let page = 1;
      while (page <= SAFETY_MAX_PAGES) {
        const res = (await unitRepo.getUnits({ page, limit: PAGE_LIMIT } as never)) as unknown as
          | PaginatedResponse<Unit>
          | Unit[];
        const pageItems = Array.isArray(res) ? res : res.data ?? [];
        accumulated.push(...pageItems);
        const total = !Array.isArray(res) ? res.meta?.total ?? accumulated.length : accumulated.length;
        const reachedEnd = pageItems.length < PAGE_LIMIT || accumulated.length >= total;
        if (reachedEnd) break;
        page += 1;
      }
      return accumulated;
    };

    fetchAllUnits()
      .then((all) => {
        if (!cancelled) setUnits(all);
      })
      .catch(() => {
        if (!cancelled) setUnits([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [unitRepo]);

  const filtered = units.filter((u) =>
    search.length < 2 ? true : getUnitLabel(u).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">¿A qué unidad pertenecerá el residente?</p>
        <button
          type="button"
          onClick={() => onToggleNewUnit(!newUnitMode)}
          className="text-xs text-primary-600 hover:underline font-medium"
        >
          {newUnitMode ? '← Seleccionar existente' : '+ Crear nueva unidad'}
        </button>
      </div>

      {newUnitMode ? (
        <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Nueva unidad</p>
          <UnitFormFields value={newUnitForm} onChange={onChangeNewUnit} />
        </div>
      ) : (
        <>
          <Input
            placeholder="Buscar unidad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {loading ? (
            <div className="flex justify-center py-6">
              <Loader size={20} className="animate-spin text-slate-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-500">
              {units.length === 0
                ? 'No hay unidades registradas. Crea una nueva.'
                : 'Sin resultados para esa búsqueda.'}
            </div>
          ) : (
            <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1">
              {filtered.map((unit) => {
                const active = selectedUnit?.id === unit.id;
                return (
                  <button
                    key={unit.id}
                    type="button"
                    onClick={() => onSelect(unit)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                      active
                        ? 'border-primary-500 bg-primary-50 text-primary-800'
                        : 'border-slate-200 hover:border-primary-300 hover:bg-primary-50/40'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{getUnitLabel(unit)}</span>
                      {active && <Check size={14} className="text-primary-600" />}
                    </div>
                    {getUnitSubLabel(unit) && (
                      <span className="text-xs text-slate-500">{getUnitSubLabel(unit)}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Step 2 — Resident Form ───────────────────────────────────────────────────

const Step2ResidentForm: React.FC<{
  unitLabel: string;
  form: ResidentForm;
  onChange: (f: ResidentForm) => void;
  onChangeIsPrincipal: (checked: boolean) => void;
  newUnitMode: boolean;
}> = ({ unitLabel, form, onChange, onChangeIsPrincipal, newUnitMode }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  const requirements = [
    { test: (p: string) => p.length >= 8, label: 'Mínimo 8 caracteres' },
    { test: (p: string) => /[A-Z]/.test(p), label: '1 mayúscula' },
    { test: (p: string) => /[a-z]/.test(p), label: '1 minúscula' },
    { test: (p: string) => /[0-9]/.test(p), label: '1 número' },
    { test: (p: string) => /[^A-Za-z0-9]/.test(p), label: '1 carácter especial' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-600">
        <span className="text-slate-400">Unidad:</span>
        <span className="font-medium text-slate-800">{unitLabel}</span>
      </div>

      <Input
        label="Nombre completo"
        value={form.name}
        onChange={(e) => onChange({ ...form, name: e.target.value })}
        required
      />
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => onChange({ ...form, email: e.target.value })}
          onBlur={() => setEmailTouched(true)}
          error={emailTouched && form.email.length > 0 && !isValidEmail(form.email) ? 'Ingresa un email válido' : undefined}
          required
        />
        <Input
          label="Teléfono (opcional)"
          value={form.phone}
          onChange={(e) => onChange({ ...form, phone: formatPhoneNumber(e.target.value) })}
          placeholder="(000) 000-0000"
        />
      </div>

      <div className="relative">
        <Input
          label="Contraseña"
          type={showPassword ? 'text' : 'password'}
          value={form.password}
          onChange={(e) => onChange({ ...form, password: e.target.value })}
          placeholder="Mínimo 8 caracteres"
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword((s) => !s)}
          className="absolute right-3 top-8 text-slate-400 hover:text-slate-600 text-xs"
        >
          {showPassword ? 'Ocultar' : 'Ver'}
        </button>
      </div>

      {form.password.length > 0 && (
        <div className="grid grid-cols-3 gap-1">
          {requirements.map((req, i) => {
            const ok = req.test(form.password);
            return (
              <span key={i} className={`text-[10px] flex items-center gap-1 ${ok ? 'text-emerald-600' : 'text-slate-400'}`}>
                {ok ? <Check size={9} /> : <span className="w-2.5" />} {req.label}
              </span>
            );
          })}
        </div>
      )}

      <div className="p-3 bg-primary-50/60 rounded-lg border border-primary-100">
        <Checkbox
          label="Será el residente principal de la unidad"
          checked={form.isPrincipal}
          onChange={onChangeIsPrincipal}
        />
        <p className="text-xs text-slate-500 mt-1 ml-6">
          {newUnitMode
            ? 'Como es una unidad nueva, lo normal es que este residente sea el principal.'
            : 'Si la unidad ya tiene un residente principal, el rol se transferirá a este residente.'}
        </p>
      </div>
    </div>
  );
};

// ─── Step 3 — RFID Form ───────────────────────────────────────────────────────

const Step3RfidForm: React.FC<{
  unitLabel: string;
  residentName: string;
  form: RfidForm;
  onChange: (f: RfidForm) => void;
}> = ({ unitLabel, residentName, form, onChange }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-600">
      <span className="font-medium text-slate-800">{residentName}</span>
      <span className="text-slate-300">·</span>
      <span className="text-slate-400">{unitLabel}</span>
    </div>

    <Checkbox
      label="Agregar credencial RFID más tarde"
      checked={form.skipRfid}
      onChange={(checked) => onChange({ ...form, skipRfid: checked })}
    />

    {!form.skipRfid && (
      <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <Input
          label="Tag ID"
          placeholder="Ej: A1B2C3D4E5"
          value={form.tagId}
          onChange={(e) => onChange({ ...form, tagId: e.target.value.toUpperCase() })}
        />
        <Input
          label="Etiqueta / descripción"
          placeholder="Ej: Llavero principal"
          value={form.label}
          onChange={(e) => onChange({ ...form, label: e.target.value })}
        />
        <Select
          label="Tipo de tarjeta"
          value={form.cardType}
          onChange={(v) => onChange({ ...form, cardType: v })}
          options={[
            { value: 'PROXIMITY', label: 'Proximidad' },
            { value: 'MIFARE', label: 'MIFARE' },
            { value: 'HID', label: 'HID' },
            { value: 'EM4100', label: 'EM4100' },
            { value: 'OTHER', label: 'Otro' },
          ]}
        />
      </div>
    )}

    {form.skipRfid && (
      <p className="text-sm text-slate-500 text-center py-2">
        Podrás asignar la credencial desde la sección <strong>Credenciales RFID</strong>.
      </p>
    )}
  </div>
);

// ─── Step 4 — Confirmation ────────────────────────────────────────────────────

interface SummaryCardProps {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, onEdit, children }) => (
  <div className="rounded-lg border border-slate-200 p-4 space-y-2">
    <div className="flex justify-between items-center">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</span>
      <button
        type="button"
        onClick={onEdit}
        className="flex items-center gap-1 text-xs text-primary-600 hover:underline"
      >
        <Pencil size={11} /> Editar
      </button>
    </div>
    <div className="text-sm text-slate-800 space-y-0.5">{children}</div>
  </div>
);

const Step4Confirmation: React.FC<{
  unitLabel: string;
  residentForm: ResidentForm;
  rfidForm: RfidForm;
  onEditUnit: () => void;
  onEditResident: () => void;
  onEditRfid: () => void;
}> = ({ unitLabel, residentForm, rfidForm, onEditUnit, onEditResident, onEditRfid }) => (
  <div className="space-y-3">
    <p className="text-sm text-slate-500">Revisa la información antes de guardar.</p>

    <SummaryCard title="Unidad" onEdit={onEditUnit}>
      <p className="font-medium">{unitLabel}</p>
    </SummaryCard>

    <SummaryCard title="Residente" onEdit={onEditResident}>
      <p className="font-medium">{residentForm.name}</p>
      <p className="text-slate-500">{residentForm.email}</p>
      {residentForm.phone && <p className="text-slate-500">{residentForm.phone}</p>}
      <p className={residentForm.isPrincipal ? 'text-primary-600 font-medium' : 'text-slate-500'}>
        {residentForm.isPrincipal ? 'Será el residente principal de la unidad' : 'No será el residente principal'}
      </p>
    </SummaryCard>

    <SummaryCard title="Credencial RFID" onEdit={onEditRfid}>
      {rfidForm.skipRfid ? (
        <p className="text-slate-400 italic">Sin RFID (se asignará después)</p>
      ) : (
        <>
          <p className="font-mono font-medium">{rfidForm.tagId}</p>
          <p className="text-slate-500">{rfidForm.label} · {rfidForm.cardType}</p>
        </>
      )}
    </SummaryCard>
  </div>
);

// ─── Wizard ───────────────────────────────────────────────────────────────────

export const ResidentOnboardingWizard: React.FC<ResidentOnboardingWizardProps> = ({
  onClose,
  onCompleted,
}) => {
  const toast = useToast();
  const { confirm } = useConfirmation();
  const { userRepo, unitRepo, rfidCredentialRepo } = useServices();
  const sectionRequired = useUnitSectionRequired();

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [newUnitMode, setNewUnitMode] = useState(false);
  const [newUnitForm, setNewUnitForm] = useState<UnitFormState>({
    type: UnitType.APARTMENT,
    tower: '',
    floor: '',
    number: '',
    street: '',
    exteriorNumber: '',
    block: '',
  });

  const [residentForm, setResidentForm] = useState<ResidentForm>({
    name: '',
    email: '',
    phone: '',
    password: '',
    isPrincipal: false,
  });
  // Solo aplicamos el default automático (marcado si es unidad nueva) la
  // primera vez que se llega al paso 2 — si el admin ya lo tocó a mano, se respeta.
  const principalTouchedRef = useRef(false);

  const [rfidForm, setRfidForm] = useState<RfidForm>({
    tagId: '',
    label: '',
    cardType: 'PROXIMITY',
    skipRfid: false,
  });

  const hasDirtyData =
    residentForm.name.length > 0 ||
    residentForm.email.length > 0 ||
    selectedUnit !== null ||
    (newUnitMode && buildUnitName(newUnitForm).length > 0);

  const handleClose = async () => {
    if (hasDirtyData) {
      const ok = await confirm({
        title: '¿Salir sin guardar?',
        message: 'Los datos ingresados se perderán.',
        confirmText: 'Salir',
        cancelText: 'Cancelar',
        variant: 'warning',
      });
      if (!ok) return;
    }
    onClose();
  };

  // Step validations
  const step1Valid = newUnitMode
    ? (!sectionRequired || !!newUnitForm.sectionId) &&
      (newUnitForm.type === UnitType.APARTMENT
        ? (newUnitForm.tower?.trim() ?? '').length > 0 && (newUnitForm.number?.trim() ?? '').length > 0
        : (newUnitForm.street?.trim() ?? '').length > 0 && (newUnitForm.exteriorNumber?.trim() ?? '').length > 0)
    : selectedUnit !== null;

  const step2Valid =
    residentForm.name.trim().length >= 2 &&
    isValidEmail(residentForm.email) &&
    isPasswordStrong(residentForm.password);

  const step3Valid = rfidForm.skipRfid || (rfidForm.tagId.trim().length >= 4 && rfidForm.label.trim().length > 0);

  const canNext = step === 1 ? step1Valid : step === 2 ? step2Valid : step === 3 ? step3Valid : false;

  const handleNext = () => {
    if (step === 1 && !principalTouchedRef.current) {
      setResidentForm((current) => ({ ...current, isPrincipal: newUnitMode }));
    }
    if (step < 4) setStep(((step + 1) as 1 | 2 | 3 | 4));
  };

  const handleBack = () => {
    if (step > 1) setStep(((step - 1) as 1 | 2 | 3 | 4));
  };

  const unitLabel = newUnitMode
    ? buildUnitName(newUnitForm) || 'Nueva unidad'
    : selectedUnit
    ? getUnitLabel(selectedUnit)
    : '—';

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Step A: create unit if new
      let finalUnitId: string;

      if (newUnitMode) {
        const created = await unitRepo.createUnit({
          type: newUnitForm.type,
          name: buildUnitName(newUnitForm),
          tower: newUnitForm.tower || undefined,
          floor: newUnitForm.floor || undefined,
          number: newUnitForm.number || undefined,
          street: newUnitForm.street || undefined,
          exteriorNumber: newUnitForm.exteriorNumber || undefined,
          interiorNumber: newUnitForm.interiorNumber || undefined,
          block: newUnitForm.block || undefined,
          sectionId: newUnitForm.sectionId || undefined,
        } as Omit<Unit, 'id'>);
        finalUnitId = created.id;
      } else {
        if (!selectedUnit) {
          toast.error('No hay unidad seleccionada. Regresa al paso 1.');
          setStep(1);
          return;
        }
        finalUnitId = selectedUnit.id;
      }

      // Step B: create resident
      const userPayload = {
        name: residentForm.name.trim(),
        email: residentForm.email.trim().toLowerCase(),
        phone: residentForm.phone.replace(/\D/g, '') || undefined,
        role: 'RESIDENT',
        status: 'ACTIVE' as const,
        password: residentForm.password,
        unitIds: [finalUnitId],
      };

      const newUser = await userRepo.createUser(userPayload as unknown as Omit<User, 'id'>);

      // Step B.5: assign as principal (optional) — el backend transfiere el
      // rol si la unidad ya tenía uno, así que basta con llamar al endpoint.
      if (residentForm.isPrincipal) {
        try {
          const unitMembers = await userRepo.getUnitHouseholdMembers(finalUnitId);
          const matchedMember = unitMembers.find(
            (member) => member.email.trim().toLowerCase() === userPayload.email,
          );
          if (matchedMember) {
            await userRepo.transferUnitPrincipal(finalUnitId, matchedMember.id);
          }
        } catch {
          toast.warning(
            'El residente fue creado, pero no se pudo marcar como principal. Hazlo desde la sección Residentes.',
            'Principal no asignado'
          );
        }
      }

      // Step C: create RFID credential (optional)
      if (!rfidForm.skipRfid) {
        try {
          await rfidCredentialRepo.createCredential({
            memberId: newUser.id,
            tagId: rfidForm.tagId.trim(),
            label: rfidForm.label.trim(),
            cardType: rfidForm.cardType as 'PROXIMITY' | 'MIFARE' | 'HID' | 'EM4100' | 'OTHER',
          });
        } catch {
          toast.warning(
            'El residente fue creado, pero no se pudo asignar el RFID. Agrégalo manualmente desde Credenciales RFID.',
            'RFID no asignado'
          );
          onCompleted(newUser);
          return;
        }
      }

      onCompleted(newUser);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al crear el residente. Intenta nuevamente.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100 shrink-0">
          <h2 className="font-bold text-slate-800 text-lg">Incorporar nuevo residente</h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Step indicator */}
        <StepIndicator current={step} />

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <Step1UnitSelector
              selectedUnit={selectedUnit}
              onSelect={(u) => { setSelectedUnit(u); setNewUnitMode(false); }}
              newUnitMode={newUnitMode}
              onToggleNewUnit={(v) => { setNewUnitMode(v); if (v) setSelectedUnit(null); }}
              newUnitForm={newUnitForm}
              onChangeNewUnit={setNewUnitForm}
            />
          )}
          {step === 2 && (
            <Step2ResidentForm
              unitLabel={unitLabel}
              form={residentForm}
              onChange={setResidentForm}
              onChangeIsPrincipal={(checked) => {
                principalTouchedRef.current = true;
                setResidentForm((current) => ({ ...current, isPrincipal: checked }));
              }}
              newUnitMode={newUnitMode}
            />
          )}
          {step === 3 && (
            <Step3RfidForm
              unitLabel={unitLabel}
              residentName={residentForm.name}
              form={rfidForm}
              onChange={setRfidForm}
            />
          )}
          {step === 4 && (
            <Step4Confirmation
              unitLabel={unitLabel}
              residentForm={residentForm}
              rfidForm={rfidForm}
              onEditUnit={() => setStep(1)}
              onEditResident={() => setStep(2)}
              onEditRfid={() => setStep(3)}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 shrink-0">
          <div>
            {step > 1 && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleBack}
                disabled={isSubmitting}
                icon={<ChevronLeft size={16} />}
              >
                Atrás
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancelar
            </Button>

            {step < 4 ? (
              <Button
                type="button"
                variant="primary"
                onClick={handleNext}
                disabled={!canNext}
              >
                Siguiente
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
                icon={isSubmitting ? <Loader size={14} className="animate-spin" /> : undefined}
              >
                {isSubmitting ? 'Guardando...' : 'Guardar e incorporar'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
